import { useLazyQuery } from "@apollo/client"
import { zodResolver } from "@hookform/resolvers/zod"
import { useEffect } from "react"
import { useForm } from "react-hook-form"
import * as z from "zod"
import { gql } from "~/__generated__"
import { useDebouncedValue } from "~/common/use-debounced-value"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { Button } from "~/ui/button"
import { Form, FormControl, FormField, FormItem, FormMessage } from "~/ui/form"
import { Input } from "~/ui/input"
import { useToast } from "~/ui/use-toast"

const addSubscriberMutation = gql(/* GraphQL */ `
  mutation AddSubscriber($input: LettermemeSubscriptionCreateInput!) {
    lettermemeSubscriptionCreate(input: $input) {
      lettermemeSubscription {
        id
        userId
        lettermemeId
        createdAt
        user {
          id
          email
        }
      }
    }
  }
`)

const userEmailSearchQuery = gql(/* GraphQL */ `
  query UserEmailSearch($email: String!, $lettermemeId: ID!) {
    userEmailSearch(email: $email, lettermemeId: $lettermemeId) {
      id
      user {
        id
        email
      }
      isUnsubscribed
    }
  }
`)

const addSubscriberFormSchema = z.object({
  email: z.string().email({ message: "Invalid email address" }),
})

type AddSubscriberFormValues = z.infer<typeof addSubscriberFormSchema>

interface AddSubscriberFormProps {
  lettermemeId: string
}

export const AddSubscriberForm: React.FC<AddSubscriberFormProps> = ({ lettermemeId }) => {
  const { toast } = useToast()

  const form = useForm<AddSubscriberFormValues>({
    resolver: zodResolver(addSubscriberFormSchema),
    defaultValues: {
      email: "",
    },
  })

  const emailValue = form.watch("email")
  const debouncedEmail = useDebouncedValue(emailValue, 300)

  const [searchUsers, { data: searchData, loading: searchLoading }] = useLazyQuery(
    userEmailSearchQuery,
    {
      fetchPolicy: "network-only",
    }
  )

  useEffect(() => {
    if (debouncedEmail && debouncedEmail.length >= 2) {
      searchUsers({ variables: { email: debouncedEmail, lettermemeId } })
    }
  }, [debouncedEmail, searchUsers, lettermemeId])

  const [addSubscriber, { loading }] = useSafeMutation(addSubscriberMutation, {
    refetchQueries: ["LettermemeSubscriptions"],
  })

  const onSubmit = async (values: AddSubscriberFormValues) => {
    try {
      await addSubscriber({
        variables: {
          input: {
            lettermemeId,
            email: values.email,
            manualSubscribe: true,
          },
        },
      })
      toast({
        title: "Subscriber added!",
        description: `${values.email} has been subscribed to this newsletter.`,
      })
      form.reset()
    } catch (e) {
      toast({
        title: "Error",
        description: "Error adding subscriber",
        variant: "destructive",
      })
      console.error(e)
    }
  }

  const matchingSubscriptions = searchData?.userEmailSearch || []
  const showMatches = matchingSubscriptions.length > 0 && debouncedEmail.length >= 2

  return (
    <div className="">
      <h2 className="mb-4 text-lg font-semibold">Add Subscriber</h2>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <div className="flex gap-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="relative flex-1">
                  <FormControl>
                    <Input
                      {...field}
                      type="email"
                      placeholder="Enter email address"
                      disabled={loading}
                      autoComplete="off"
                    />
                  </FormControl>
                  <FormMessage />
                  {showMatches && (
                    <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
                      <div className="py-1 text-black">
                        {searchLoading && (
                          <div className="px-4 py-2 text-sm text-gray-500">Searching...</div>
                        )}
                        {!searchLoading &&
                          matchingSubscriptions.map((subscription) => (
                            <button
                              key={subscription.id}
                              type="button"
                              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100"
                              onClick={() => {
                                form.setValue("email", subscription.user.email)
                              }}
                            >
                              <span>{subscription.user.email}</span>
                              {subscription.isUnsubscribed && (
                                <span className="ml-2 text-gray-500">(unsubscribed)</span>
                              )}
                            </button>
                          ))}
                      </div>
                    </div>
                  )}
                </FormItem>
              )}
            />
            <Button type="submit" disabled={loading}>
              {loading ? "Adding..." : "Add Subscriber"}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
